import React, {

    useEffect,

    useState

}

from "react";

import {

    FontAwesomeIcon

}

from "@fortawesome/react-fontawesome";

import {

    faCheck,

    faImage,

    faXmark,

    faExpand

}

from "@fortawesome/free-solid-svg-icons";

import {

    functions

}

from "../../../constants/common-functions";

import {

    OMNISCAN_BLUE

}

from "../../../constants/colors";


//========================================================
// Dynamic Snippet Selection Drawer
//
// Displays the structural content persisted by Forager for
// a single source-document page.
//
// Sections may contain text, images or other supported page
// content types.
//
// Images are retrieved separately using authenticated blob
// requests and displayed as thumbnails.
//
// Selected sections retain their source section ID and can
// subsequently be persisted as virtual-document selections.
//========================================================

export default function DynamicSnippetSelectionDrawer({

    visible,

    documentTitle = "Document",

    pageNumber = 1,

    documentSnippetId,

    sections = [],

    onClose,

    onAddSelected

})

{

    //========================================================
    // Selection
    //
    // Selection is based on the persistent Forager section
    // ID rather than the current array position.
    //========================================================

    const [selectedSectionIds, setSelectedSectionIds] =

        useState([]);


    //========================================================
    // Hover
    //========================================================

    const [hoveredSectionId, setHoveredSectionId] =

        useState(null);


    //========================================================
    // Image URLs
    //
    // Maps:
    //
    // sectionId -> browser object URL
    //
    // The actual image is retrieved using our authenticated
    // AJAX helper rather than using the API URL directly as
    // an <img> source.
    //========================================================

    const [imageUrls, setImageUrls] =

        useState({});


    //========================================================
    // Image Preview
    //========================================================

    const [previewSection, setPreviewSection] =

        useState(null);


    //========================================================
    // Safe Sections
    //========================================================

    const safeSections =

        Array.isArray(

            sections

        )

            ?

            sections

            :

            [];


    //========================================================
    // Section Type
    //========================================================

    const getSectionType = (section) =>
    {

        return (

            section?.sectionType ||

            ""

        )
            .toUpperCase();

    };


    //========================================================
    // Is Image
    //========================================================

    const isImageSection = (section) =>
    {

        return (

            getSectionType(

                section

            ) === "IMAGE" ||

            section?.hasImage === true ||

            (

                section?.contentType &&

                section.contentType

                    .toLowerCase()

                    .startsWith(

                        "image/"

                    )

            )

        );

    };


    //========================================================
    // Reset Selection
    //
    // Every time the drawer opens for a page we begin with
    // a clean selection.
    //========================================================

    useEffect(() =>
    {

        if (

            visible

        )

        {

            setSelectedSectionIds(

                []

            );


            setHoveredSectionId(

                null

            );


            setPreviewSection(

                null

            );

        }

    }, [

        visible,

        documentSnippetId,

        pageNumber

    ]);


    //========================================================
    // Load Image Sections
    //
    // Images cannot be referenced directly from <img src>
    // because the API requires our normal authentication
    // headers.
    //
    // Retrieve each image as a Blob and create a temporary
    // browser object URL for the thumbnail and modal.
    //========================================================

    useEffect(() =>
    {

        //====================================================
        // Not Visible
        //====================================================

        if (

            !visible

        )

        {

            setImageUrls(

                {}

            );

            return;

        }


        //====================================================
        // Image Sections
        //====================================================

        const imageSections =

            (
                Array.isArray(
                    sections
                )
                    ?
                    sections
                    :
                    []
            )
                .filter(
                    section =>
                    {

                        const sectionType =

                            (
                                section?.sectionType ||
                                ""
                            )
                                .toUpperCase();


                        return (

                            sectionType === "IMAGE" ||

                            section?.hasImage === true ||

                            (

                                section?.contentType &&

                                section.contentType

                                    .toLowerCase()

                                    .startsWith(

                                        "image/"

                                    )

                            )

                        );

                    }
                )
                .filter(
                    section =>
                        section?.id != null
                );


        //====================================================
        // No Images
        //====================================================

        if (

            imageSections.length === 0

        )

        {

            setImageUrls(

                {}

            );

            return;

        }


        //====================================================
        // Lifecycle
        //====================================================

        let cancelled =

            false;


        const objectUrls =

            [];


        setImageUrls(

            {}

        );


        //====================================================
        // Load Images
        //====================================================

        imageSections.forEach(

            section =>
            {

                const url =

                    "/resolutionsoftwareplatform/snippet-sniffer/sections/" +

                    section.id +

                    "/image";


                functions.getBlobFromAjax(

                    url,

                    function(blob)

                    {

                        if (

                            cancelled ||

                            !blob

                        )

                        {

                            return;

                        }


                        //========================================
                        // Create Browser URL
                        //========================================

                        const objectUrl =

                            window.URL.createObjectURL(

                                blob

                            );


                        objectUrls.push(

                            objectUrl

                        );


                        //========================================
                        // Store Against Section
                        //========================================

                        setImageUrls(

                            current =>
                            ({

                                ...current,

                                [section.id]:
                                    objectUrl

                            })

                        );

                    }

                );

            }

        );


        //====================================================
        // Cleanup
        //====================================================

        return () =>
        {

            cancelled =

                true;


            objectUrls.forEach(

                objectUrl =>
                {

                    window.URL.revokeObjectURL(

                        objectUrl

                    );

                }

            );

        };

    }, [

        visible,

        documentSnippetId,

        pageNumber,

        sections

    ]);


    //========================================================
    // Escape Image Preview
    //========================================================

    useEffect(() =>
    {

        if (

            !previewSection

        )

        {

            return;

        }


        const handleKeyDown = (event) =>
        {

            if (

                event.key === "Escape"

            )

            {

                setPreviewSection(

                    null

                );

            }

        };


        window.addEventListener(

            "keydown",

            handleKeyDown

        );


        return () =>
        {

            window.removeEventListener(

                "keydown",

                handleKeyDown

            );

        };

    }, [

        previewSection

    ]);


    //========================================================
    // Is Selected
    //========================================================

    const isSelected = (section) =>
    {

        return selectedSectionIds.includes(

            section?.id

        );

    };


    //========================================================
    // Toggle Selection
    //========================================================

    const toggleSelection = (section) =>
    {

        if (

            section?.id == null

        )

        {

            return;

        }


        setSelectedSectionIds(

            current =>
            {

                if (

                    current.includes(

                        section.id

                    )

                )

                {

                    return current.filter(

                        sectionId =>
                            sectionId !== section.id

                    );

                }


                return [

                    ...current,

                    section.id

                ];

            }

        );

    };


    //========================================================
    // Select All
    //========================================================

    const selectAll = () =>
    {

        setSelectedSectionIds(

            safeSections

                .filter(

                    section =>
                        section?.id != null

                )

                .map(

                    section =>
                        section.id

                )

        );

    };


    //========================================================
    // Clear Selection
    //========================================================

    const clearSelection = () =>
    {

        setSelectedSectionIds(

            []

        );

    };


    //========================================================
    // Open Image Preview
    //========================================================

    const openImagePreview = (

        event,

        section

    ) =>
    {

        //====================================================
        // Do Not Toggle Section Selection
        //====================================================

        event.stopPropagation();


        if (

            !imageUrls[
                section?.id
            ]

        )

        {

            return;

        }


        setPreviewSection(

            section

        );

    };


    //========================================================
    // Add Selected
    //========================================================

    const addSelected = () =>
    {

        if (

            !onAddSelected ||

            selectedSectionIds.length === 0

        )

        {

            return;

        }


        const selectedSections =

            safeSections

                .filter(

                    section =>
                        selectedSectionIds.includes(

                            section?.id

                        )

                )

                .slice()

                .sort(

                    (a, b) =>
                        (a?.sequence || 0) -
                        (b?.sequence || 0)

                )

                .map(

                    section =>
                    {

                        const image =

                            isImageSection(

                                section

                            );


                        return {

                            ...section,

                            documentSnippetId,

                            pageNumber,

                            selectionType:

                                "PARTIAL",

                            documentSnippetSectionId:

                                section.id,

                            selectedText:

                                image

                                    ?

                                    null

                                    :

                                    section?.textContent ||
                                    null

                        };

                    }

                );


        onAddSelected(

            selectedSections

        );

    };


    //========================================================
    // Content Counts
    //========================================================

    const textSectionCount =

        safeSections.filter(

            section =>
                !isImageSection(

                    section

                )

        ).length;


    const imageSectionCount =

        safeSections.filter(

            section =>
                isImageSection(

                    section

                )

        ).length;


    //========================================================
    // Drawer Style
    //========================================================

    const drawerStyle =
    {

        ...styles.drawer,

        transform:

            visible

                ?

                "translateX(0)"

                :

                "translateX(-104%)",

        opacity:

            visible

                ?

                1

                :

                0,

        pointerEvents:

            visible

                ?

                "auto"

                :

                "none"

    };


    //========================================================
    // Render
    //========================================================

    return (

        <div style={drawerStyle}>


            {/*============================================
                Header
            ============================================*/}

            <div style={styles.header}>

                <div style={styles.headerText}>

                    <div style={styles.eyebrow}>

                        SELECT PAGE CONTENT

                    </div>

                    <div style={styles.title}>

                        {

                            documentTitle ||

                            "Untitled Document"

                        }

                    </div>

                    <div style={styles.pageText}>

                        Page {pageNumber}

                    </div>

                </div>

            </div>


            {/*============================================
                Selection Toolbar
            ============================================*/}

            <div style={styles.toolbar}>

                <div style={styles.toolbarSummary}>

                    <span style={styles.snippetCount}>

                        {safeSections.length}

                    </span>

                    <span>

                        {

                            safeSections.length === 1

                                ?

                                " section"

                                :

                                " sections"

                        }

                    </span>


                    {

                        safeSections.length > 0 &&

                        (

                            <span style={styles.contentBreakdown}>

                                (

                                {textSectionCount} text

                                {

                                    imageSectionCount > 0 &&

                                    (

                                        <>

                                            {" · "}

                                            {imageSectionCount} image

                                        </>

                                    )

                                }

                                )

                            </span>

                        )

                    }

                </div>


                <div style={styles.toolbarActions}>

                    <button

                        type="button"

                        style={styles.textButton}

                        onClick={

                            selectAll

                        }

                        disabled={

                            safeSections.length === 0

                        }

                    >

                        Select All

                    </button>


                    <div style={styles.toolbarDivider} />


                    <button

                        type="button"

                        style={
                            {

                                ...styles.textButton,

                                ...(selectedSectionIds.length === 0

                                    ?

                                    styles.textButtonDisabled

                                    :

                                    {})

                            }
                        }

                        onClick={

                            clearSelection

                        }

                        disabled={

                            selectedSectionIds.length === 0

                        }

                    >

                        Clear

                    </button>

                </div>

            </div>


            {/*============================================
                Scrollable Section List
            ============================================*/}

            <div style={styles.list}>

                {

                    safeSections.length === 0 &&

                    (

                        <div style={styles.empty}>

                            <div style={styles.emptyTitle}>

                                No Selectable Content

                            </div>

                            <div style={styles.emptyText}>

                                No selectable sections are available
                                for this page.

                            </div>

                        </div>

                    )

                }


                {

                    safeSections.map(

                        (section, index) =>
                        {

                            const selected =

                                isSelected(

                                    section

                                );


                            const hovered =

                                hoveredSectionId ===

                                section?.id;


                            const image =

                                isImageSection(

                                    section

                                );


                            const imageUrl =

                                imageUrls[
                                    section?.id
                                ];


                            return (

                                <button

                                    key={

                                        section?.id ||

                                        index

                                    }

                                    type="button"

                                    style={
                                        {

                                            ...styles.snippetCard,

                                            ...(hovered

                                                ?

                                                styles.snippetCardHover

                                                :

                                                {}),

                                            ...(selected

                                                ?

                                                styles.snippetCardSelected

                                                :

                                                {})

                                        }
                                    }

                                    onClick={
                                        () =>
                                        {

                                            toggleSelection(

                                                section

                                            );

                                        }
                                    }

                                    onMouseEnter={
                                        () =>
                                        {

                                            setHoveredSectionId(

                                                section?.id

                                            );

                                        }
                                    }

                                    onMouseLeave={
                                        () =>
                                        {

                                            setHoveredSectionId(

                                                null

                                            );

                                        }
                                    }

                                >


                                    {/*================================
                                        Selection Column
                                    =================================*/}

                                    <div style={styles.selectionColumn}>

                                        <div

                                            style={
                                                {

                                                    ...styles.checkbox,

                                                    ...(selected

                                                        ?

                                                        styles.checkboxSelected

                                                        :

                                                        {})

                                                }
                                            }

                                        >

                                            {

                                                selected &&

                                                (

                                                    <FontAwesomeIcon

                                                        icon={faCheck}

                                                    />

                                                )

                                            }

                                        </div>


                                        <div style={styles.index}>

                                            {

                                                String(

                                                    section?.sequence ||

                                                    index + 1

                                                ).padStart(

                                                    2,

                                                    "0"

                                                )

                                            }

                                        </div>

                                    </div>


                                    {/*================================
                                        Image Content
                                    =================================*/}

                                    {

                                        image &&

                                        (

                                            <div style={styles.snippetContent}>

                                                <div style={styles.imageContent}>


                                                    {/*========================
                                                        Thumbnail
                                                    ========================*/}

                                                    <div

                                                        style={
                                                            styles.imageThumbnailContainer
                                                        }

                                                        onClick={
                                                            event =>
                                                            {

                                                                openImagePreview(

                                                                    event,

                                                                    section

                                                                );

                                                            }
                                                        }

                                                        title={
                                                            imageUrl

                                                                ?

                                                                "Click to enlarge"

                                                                :

                                                                "Loading image"
                                                        }

                                                    >

                                                        {

                                                            imageUrl

                                                                ?

                                                                (

                                                                    <img

                                                                        src={
                                                                            imageUrl
                                                                        }

                                                                        alt={
                                                                            "Page " +
                                                                            pageNumber +
                                                                            " image " +
                                                                            (
                                                                                section?.sequence ||
                                                                                index + 1
                                                                            )
                                                                        }

                                                                        style={
                                                                            styles.imageThumbnail
                                                                        }

                                                                    />

                                                                )

                                                                :

                                                                (

                                                                    <div style={styles.imageLoading}>

                                                                        <FontAwesomeIcon

                                                                            icon={faImage}

                                                                        />

                                                                    </div>

                                                                )

                                                        }


                                                        {

                                                            imageUrl &&

                                                            (

                                                                <div style={styles.imageExpandBadge}>

                                                                    <FontAwesomeIcon

                                                                        icon={faExpand}

                                                                    />

                                                                </div>

                                                            )

                                                        }

                                                    </div>


                                                    {/*========================
                                                        Image Details
                                                    ========================*/}

                                                    <div style={styles.imageDetails}>

                                                        <div style={styles.imageTitle}>

                                                            Image

                                                        </div>


                                                        <div style={styles.imageMeta}>

                                                            {

                                                                section?.contentType ||

                                                                "Image content"

                                                            }


                                                            {

                                                                section?.width != null &&

                                                                section?.height != null &&

                                                                (

                                                                    <>

                                                                        {" · "}

                                                                        {
                                                                            Math.round(
                                                                                section.width
                                                                            )
                                                                        }

                                                                        {" × "}

                                                                        {
                                                                            Math.round(
                                                                                section.height
                                                                            )
                                                                        }

                                                                    </>

                                                                )

                                                            }

                                                        </div>


                                                        <div style={styles.imageHint}>

                                                            {

                                                                imageUrl

                                                                    ?

                                                                    "Click thumbnail to enlarge"

                                                                    :

                                                                    "Loading preview..."

                                                            }

                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                        )

                                    }


                                    {/*================================
                                        Text Content
                                    =================================*/}

                                    {

                                        !image &&

                                        (

                                            <div style={styles.snippetContent}>

                                                <div style={styles.snippetText}>

                                                    {

                                                        section?.textContent ||

                                                        "No text available."

                                                    }

                                                </div>

                                            </div>

                                        )

                                    }

                                </button>

                            );

                        }

                    )

                }

            </div>


            {/*============================================
                Footer
            ============================================*/}

            <div style={styles.footer}>


                {/*========================================
                    Cancel
                ========================================*/}

                <button

                    type="button"

                    style={

                        styles.cancelButton

                    }

                    onClick={

                        onClose

                    }

                >

                    Cancel

                </button>


                {/*========================================
                    Selection Actions
                ========================================*/}

                <div style={styles.footerActions}>

                    <div style={styles.selectionSummary}>

                        <span style={styles.selectedCount}>

                            {selectedSectionIds.length}

                        </span>

                        <span>

                            {

                                selectedSectionIds.length === 1

                                    ?

                                    " section selected"

                                    :

                                    " sections selected"

                            }

                        </span>

                    </div>


                    <button

                        type="button"

                        style={
                            {

                                ...styles.primaryButton,

                                ...(selectedSectionIds.length === 0

                                    ?

                                    styles.primaryButtonDisabled

                                    :

                                    {})

                            }
                        }

                        onClick={

                            addSelected

                        }

                        disabled={

                            selectedSectionIds.length === 0

                        }

                    >

                        Add Selected to Basket

                    </button>

                </div>

            </div>


            {/*============================================
                Image Preview Modal
            ============================================*/}

            {

                previewSection &&

                imageUrls[
                    previewSection.id
                ] &&

                (

                    <div

                        style={styles.modalBackdrop}

                        onClick={
                            () =>
                            {

                                setPreviewSection(

                                    null

                                );

                            }
                        }

                    >

                        <div

                            style={styles.modal}

                            onClick={
                                event =>
                                {

                                    event.stopPropagation();

                                }
                            }

                        >


                            {/*================================
                                Modal Header
                            =================================*/}

                            <div style={styles.modalHeader}>

                                <div style={styles.modalHeaderText}>

                                    <div style={styles.modalEyebrow}>

                                        SOURCE IMAGE

                                    </div>

                                    <div style={styles.modalTitle}>

                                        {

                                            documentTitle ||

                                            "Untitled Document"

                                        }

                                    </div>

                                    <div style={styles.modalMeta}>

                                        Page {pageNumber}

                                        {" · "}

                                        Section {
                                            previewSection?.sequence ||
                                            ""
                                        }

                                        {

                                            previewSection?.contentType &&

                                            (

                                                <>

                                                    {" · "}

                                                    {
                                                        previewSection.contentType
                                                    }

                                                </>

                                            )

                                        }

                                    </div>

                                </div>


                                <button

                                    type="button"

                                    style={styles.modalCloseButton}

                                    onClick={
                                        () =>
                                        {

                                            setPreviewSection(

                                                null

                                            );

                                        }
                                    }

                                    title="Close image preview"

                                >

                                    <FontAwesomeIcon

                                        icon={faXmark}

                                    />

                                </button>

                            </div>


                            {/*================================
                                Modal Image
                            =================================*/}

                            <div style={styles.modalContent}>

                                <img

                                    src={
                                        imageUrls[
                                            previewSection.id
                                        ]
                                    }

                                    alt="Source document content"

                                    style={styles.modalImage}

                                />

                            </div>

                        </div>

                    </div>

                )

            }

        </div>

    );

}


//========================================================
// Styles
//========================================================

const styles =
{

    //========================================================
    // Drawer
    //========================================================

    drawer:
    {

        position:
            "absolute",

        top:
            0,

        right:
            0,

        bottom:
            0,

        left:
            0,

        width:
            "100%",

        height:
            "100%",

        display:
            "flex",

        flexDirection:
            "column",

        background:
            "#f4f7f9",

        borderRight:
            "1px solid rgba(38,55,70,0.12)",

        boxShadow:
            "12px 0 32px rgba(20,35,50,0.14)",

        zIndex:
            50,

        overflow:
            "hidden",

        boxSizing:
            "border-box",

        transition:
            "transform 620ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease"

    },


    //========================================================
    // Header
    //========================================================

    header:
    {

        minHeight:
            92,

        padding:
            "18px 22px",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        boxSizing:
            "border-box",

        background:
            "#ffffff",

        borderBottom:
            "1px solid rgba(38,55,70,0.08)"

    },

    headerText:
    {

        minWidth:
            0,

        paddingRight:
            20

    },

    eyebrow:
    {

        marginBottom:
            5,

        fontSize:
            10,

        fontWeight:
            700,

        letterSpacing:
            1.4,

        color:
            OMNISCAN_BLUE

    },

    title:
    {

        maxWidth:
            600,

        fontSize:
            16,

        fontWeight:
            600,

        color:
            "#263746",

        whiteSpace:
            "nowrap",

        overflow:
            "hidden",

        textOverflow:
            "ellipsis"

    },

    pageText:
    {

        marginTop:
            4,

        fontSize:
            11,

        fontWeight:
            500,

        color:
            "rgba(38,55,70,0.48)"

    },


    //========================================================
    // Toolbar
    //========================================================

    toolbar:
    {

        height:
            54,

        minHeight:
            54,

        padding:
            "0 22px",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        background:
            "#ffffff",

        borderBottom:
            "1px solid rgba(38,55,70,0.08)"

    },

    toolbarSummary:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            5,

        fontSize:
            11,

        fontWeight:
            500,

        color:
            "rgba(38,55,70,0.55)"

    },

    snippetCount:
    {

        fontWeight:
            700,

        color:
            "#263746"

    },

    contentBreakdown:
    {

        marginLeft:
            3,

        color:
            "rgba(38,55,70,0.42)"

    },

    toolbarActions:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            10

    },

    toolbarDivider:
    {

        width:
            1,

        height:
            16,

        background:
            "rgba(38,55,70,0.12)"

    },

    textButton:
    {

        padding:
            "5px 2px",

        border:
            "none",

        background:
            "transparent",

        color:
            OMNISCAN_BLUE,

        fontSize:
            11,

        fontWeight:
            600,

        cursor:
            "pointer"

    },

    textButtonDisabled:
    {

        opacity:
            0.35,

        cursor:
            "default"

    },


    //========================================================
    // List
    //========================================================

    list:
    {

        flex:
            1,

        minHeight:
            0,

        padding:
            "18px 22px",

        overflowY:
            "auto",

        overflowX:
            "hidden",

        boxSizing:
            "border-box"

    },


    //========================================================
    // Section Card
    //========================================================

    snippetCard:
    {

        width:
            "100%",

        marginBottom:
            12,

        padding:
            0,

        display:
            "flex",

        alignItems:
            "stretch",

        textAlign:
            "left",

        background:
            "#ffffff",

        border:
            "1px solid rgba(38,55,70,0.10)",

        borderRadius:
            6,

        boxShadow:
            "0 3px 10px rgba(20,35,50,0.04)",

        overflow:
            "hidden",

        cursor:
            "pointer",

        transition:
            "border-color 160ms ease, background 160ms ease, box-shadow 160ms ease, transform 160ms ease"

    },

    snippetCardHover:
    {

        border:
            "1px solid rgba(32,175,232,0.34)",

        boxShadow:
            "0 6px 16px rgba(20,35,50,0.08)",

        transform:
            "translateY(-1px)"

    },

    snippetCardSelected:
    {

        background:
            "rgba(32,175,232,0.055)",

        border:
            "1px solid rgba(32,175,232,0.68)",

        boxShadow:
            "0 5px 16px rgba(32,175,232,0.10)"

    },


    //========================================================
    // Selection Column
    //========================================================

    selectionColumn:
    {

        width:
            58,

        minWidth:
            58,

        padding:
            "16px 0",

        display:
            "flex",

        flexDirection:
            "column",

        alignItems:
            "center",

        gap:
            10,

        background:
            "rgba(38,55,70,0.025)",

        borderRight:
            "1px solid rgba(38,55,70,0.07)",

        boxSizing:
            "border-box"

    },

    checkbox:
    {

        width:
            20,

        height:
            20,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        border:
            "1px solid rgba(38,55,70,0.24)",

        borderRadius:
            3,

        background:
            "#ffffff",

        color:
            "#ffffff",

        fontSize:
            10,

        boxSizing:
            "border-box",

        transition:
            "background 150ms ease, border-color 150ms ease"

    },

    checkboxSelected:
    {

        background:
            OMNISCAN_BLUE,

        border:
            "1px solid " + OMNISCAN_BLUE

    },

    index:
    {

        fontSize:
            9,

        fontWeight:
            700,

        letterSpacing:
            0.8,

        color:
            "rgba(38,55,70,0.38)"

    },


    //========================================================
    // Section Content
    //========================================================

    snippetContent:
    {

        flex:
            1,

        minWidth:
            0,

        padding:
            "16px 18px"

    },

    snippetText:
    {

        fontSize:
            13,

        lineHeight:
            1.65,

        fontWeight:
            400,

        color:
            "#263746",

        whiteSpace:
            "pre-wrap"

    },


    //========================================================
    // Image Content
    //========================================================

    imageContent:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            18,

        minHeight:
            100

    },

    imageThumbnailContainer:
    {

        position:
            "relative",

        width:
            128,

        height:
            92,

        minWidth:
            128,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        overflow:
            "hidden",

        border:
            "1px solid rgba(38,55,70,0.10)",

        borderRadius:
            5,

        background:
            "#f4f7f9",

        boxShadow:
            "0 3px 10px rgba(20,35,50,0.05)",

        cursor:
            "zoom-in"

    },

    imageThumbnail:
    {

        width:
            "100%",

        height:
            "100%",

        display:
            "block",

        objectFit:
            "contain",

        background:
            "#ffffff"

    },

    imageLoading:
    {

        width:
            "100%",

        height:
            "100%",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        color:
            OMNISCAN_BLUE,

        fontSize:
            24,

        background:
            "rgba(32,175,232,0.06)"

    },

    imageExpandBadge:
    {

        position:
            "absolute",

        right:
            5,

        bottom:
            5,

        width:
            24,

        height:
            24,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        borderRadius:
            4,

        background:
            "rgba(38,55,70,0.82)",

        color:
            "#ffffff",

        fontSize:
            10,

        pointerEvents:
            "none"

    },

    imageDetails:
    {

        minWidth:
            0

    },

    imageTitle:
    {

        marginBottom:
            5,

        fontSize:
            13,

        fontWeight:
            600,

        color:
            "#263746"

    },

    imageMeta:
    {

        fontSize:
            11,

        fontWeight:
            400,

        color:
            "rgba(38,55,70,0.48)"

    },

    imageHint:
    {

        marginTop:
            6,

        fontSize:
            10,

        fontWeight:
            500,

        color:
            OMNISCAN_BLUE

    },


    //========================================================
    // Empty
    //========================================================

    empty:
    {

        height:
            "100%",

        minHeight:
            200,

        display:
            "flex",

        flexDirection:
            "column",

        alignItems:
            "center",

        justifyContent:
            "center"

    },

    emptyTitle:
    {

        marginBottom:
            6,

        fontSize:
            14,

        fontWeight:
            600,

        color:
            "#263746"

    },

    emptyText:
    {

        fontSize:
            12,

        color:
            "rgba(38,55,70,0.48)"

    },


    //========================================================
    // Footer
    //========================================================

    footer:
    {

        height:
            72,

        minHeight:
            72,

        padding:
            "0 22px",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        background:
            "#ffffff",

        borderTop:
            "1px solid rgba(38,55,70,0.08)"

    },

    selectionSummary:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            5,

        fontSize:
            11,

        color:
            "rgba(38,55,70,0.55)"

    },

    selectedCount:
    {

        minWidth:
            22,

        height:
            22,

        padding:
            "0 6px",

        display:
            "inline-flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        boxSizing:
            "border-box",

        borderRadius:
            3,

        background:
            "rgba(32,175,232,0.10)",

        color:
            OMNISCAN_BLUE,

        fontWeight:
            700

    },


    //========================================================
    // Footer Actions
    //========================================================

    footerActions:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            18

    },


    //========================================================
    // Cancel Button
    //========================================================

    cancelButton:
    {

        height:
            40,

        padding:
            "0 18px",

        border:
            "1px solid rgba(38,55,70,0.14)",

        borderRadius:
            6,

        background:
            "#f4f7f9",

        color:
            "#263746",

        fontSize:
            12,

        fontWeight:
            600,

        cursor:
            "pointer"

    },


    //========================================================
    // Primary Button
    //========================================================

    primaryButton:
    {

        height:
            40,

        padding:
            "0 18px",

        border:
            "none",

        borderRadius:
            6,

        background:
            OMNISCAN_BLUE,

        color:
            "#ffffff",

        fontSize:
            12,

        fontWeight:
            600,

        cursor:
            "pointer",

        transition:
            "opacity 160ms ease"

    },

    primaryButtonDisabled:
    {

        opacity:
            0.35,

        cursor:
            "default"

    },


    //========================================================
    // Image Modal Backdrop
    //========================================================

    modalBackdrop:
    {

        position:
            "fixed",

        top:
            0,

        right:
            0,

        bottom:
            0,

        left:
            0,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        padding:
            40,

        boxSizing:
            "border-box",

        background:
            "rgba(15,25,35,0.78)",

        backdropFilter:
            "blur(3px)",

        zIndex:
            5000,

        cursor:
            "zoom-out"

    },


    //========================================================
    // Image Modal
    //========================================================

    modal:
    {

        width:
            "min(1100px, 92vw)",

        height:
            "min(820px, 90vh)",

        display:
            "flex",

        flexDirection:
            "column",

        overflow:
            "hidden",

        borderRadius:
            8,

        background:
            "#ffffff",

        boxShadow:
            "0 24px 70px rgba(0,0,0,0.34)",

        cursor:
            "default"

    },


    //========================================================
    // Modal Header
    //========================================================

    modalHeader:
    {

        minHeight:
            76,

        padding:
            "14px 18px 14px 22px",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        boxSizing:
            "border-box",

        background:
            "#ffffff",

        borderBottom:
            "1px solid rgba(38,55,70,0.08)"

    },

    modalHeaderText:
    {

        minWidth:
            0,

        paddingRight:
            20

    },

    modalEyebrow:
    {

        marginBottom:
            4,

        fontSize:
            9,

        fontWeight:
            700,

        letterSpacing:
            1.3,

        color:
            OMNISCAN_BLUE

    },

    modalTitle:
    {

        maxWidth:
            850,

        fontSize:
            15,

        fontWeight:
            600,

        color:
            "#263746",

        whiteSpace:
            "nowrap",

        overflow:
            "hidden",

        textOverflow:
            "ellipsis"

    },

    modalMeta:
    {

        marginTop:
            3,

        fontSize:
            10,

        color:
            "rgba(38,55,70,0.48)"

    },

    modalCloseButton:
    {

        width:
            36,

        height:
            36,

        minWidth:
            36,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        padding:
            0,

        border:
            "1px solid rgba(38,55,70,0.12)",

        borderRadius:
            "50%",

        background:
            "#f4f7f9",

        color:
            "#263746",

        fontSize:
            14,

        cursor:
            "pointer"

    },


    //========================================================
    // Modal Content
    //========================================================

    modalContent:
    {

        flex:
            1,

        minHeight:
            0,

        padding:
            24,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        overflow:
            "auto",

        boxSizing:
            "border-box",

        background:
            "#eef2f5"

    },

    modalImage:
    {

        maxWidth:
            "100%",

        maxHeight:
            "100%",

        width:
            "auto",

        height:
            "auto",

        display:
            "block",

        objectFit:
            "contain",

        background:
            "#ffffff",

        boxShadow:
            "0 8px 30px rgba(20,35,50,0.14)"

    }

};